import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUID } from '@/utils/helper';
import styles from './SpinWheelModal.module.css';

const API_BASE = 'https://api.cryptmet.com';

const SEGMENT_COLORS = [
  '#C1121F', '#2563EB', '#7B2D8E', '#B8860B', '#BE185D',
  '#D4572A', '#4338CA', '#8B5CF6', '#CA8A04', '#1D4ED8',
  '#9F1239', '#6D28D9', '#DC2626', '#7C3AED', '#B45309',
  '#DB2777', '#4F46E5', '#A21CAF',
];

const LED_COUNT = 36;

const formatTime = (ms) => {
  if (ms <= 0) return '00:00:00';
  const sec = Math.floor(ms / 1000);
  const h = String(Math.floor(sec / 3600)).padStart(2, '0');
  const m = String(Math.floor((sec % 3600) / 60)).padStart(2, '0');
  const s = String(sec % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

function drawWheel(ctx, size, segments, rotation, ledPhase) {
  if (!segments || segments.length === 0) return;
  const total = segments.length;
  const arc = (2 * Math.PI) / total;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = cx - 4;
  const goldR = outerR - 14;
  const innerR = goldR - 6;
  const segR = innerR - 3;
  const centerR = size * 0.13;

  ctx.clearRect(0, 0, size, size);

  // 1. Outer ring — deep navy/indigo
  ctx.beginPath();
  ctx.arc(cx, cy, outerR, 0, 2 * Math.PI);
  const outerGrad = ctx.createRadialGradient(cx, cy, goldR, cx, cy, outerR);
  outerGrad.addColorStop(0, '#1E1B4B');
  outerGrad.addColorStop(0.5, '#312E81');
  outerGrad.addColorStop(1, '#1E1B4B');
  ctx.fillStyle = outerGrad;
  ctx.fill();

  // 2. LED dots
  for (let i = 0; i < LED_COUNT; i++) {
    const angle = (i / LED_COUNT) * 2 * Math.PI - Math.PI / 2;
    const lx = cx + Math.cos(angle) * (outerR - 9);
    const ly = cy + Math.sin(angle) * (outerR - 9);
    const isLit = (i + ledPhase) % 3 === 0;
    ctx.beginPath();
    ctx.arc(lx, ly, 3.5, 0, 2 * Math.PI);
    ctx.fillStyle = isLit ? '#F5E27A' : 'rgba(245,226,122,0.2)';
    ctx.fill();
    if (isLit) {
      ctx.shadowColor = '#F5D76E';
      ctx.shadowBlur = 10;
      ctx.fill();
      ctx.shadowBlur = 0;
    }
  }

  // 3. Gold border ring
  ctx.beginPath();
  ctx.arc(cx, cy, goldR, 0, 2 * Math.PI);
  const goldGrad = ctx.createLinearGradient(cx - goldR, cy - goldR, cx + goldR, cy + goldR);
  goldGrad.addColorStop(0, '#F5D76E');
  goldGrad.addColorStop(0.3, '#D4AF37');
  goldGrad.addColorStop(0.6, '#F5E27A');
  goldGrad.addColorStop(1, '#C5A028');
  ctx.fillStyle = goldGrad;
  ctx.fill();

  // 4. Inner ring — dark indigo
  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, 2 * Math.PI);
  ctx.fillStyle = '#1E1B4B';
  ctx.fill();

  // 5. Segments
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);

  segments.forEach((seg, i) => {
    const start = i * arc - Math.PI / 2;
    const end = start + arc;

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, segR, start, end);
    ctx.closePath();
    ctx.fillStyle = seg.color;
    ctx.fill();

    // Divider line
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(start) * segR, Math.sin(start) * segR);
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Inner shine
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, segR, start, end);
    ctx.closePath();
    const shineGrad = ctx.createRadialGradient(0, 0, segR * 0.6, 0, 0, segR);
    shineGrad.addColorStop(0, 'rgba(255,255,255,0)');
    shineGrad.addColorStop(1, 'rgba(255,255,255,0.08)');
    ctx.fillStyle = shineGrad;
    ctx.fill();

    // Label
    ctx.save();
    ctx.rotate(start + arc / 2);
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#fff';
    ctx.font = `bold ${size < 380 ? 9 : 11}px Montserrat, Inter, sans-serif`;
    ctx.shadowColor = 'rgba(0,0,0,0.7)';
    ctx.shadowBlur = 4;
    ctx.fillText(seg.label, segR - 12, 0);
    ctx.shadowBlur = 0;
    ctx.restore();
  });

  ctx.restore();

  // 6. Center button
  ctx.save();
  ctx.translate(cx, cy);

  ctx.beginPath();
  ctx.arc(0, 0, centerR + 6, 0, 2 * Math.PI);
  ctx.fillStyle = 'rgba(193,18,31,0.3)';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, 0, centerR + 3, 0, 2 * Math.PI);
  ctx.fillStyle = '#D4AF37';
  ctx.fill();

  const cGrad = ctx.createRadialGradient(0, -centerR * 0.3, 0, 0, 0, centerR);
  cGrad.addColorStop(0, '#DC2626');
  cGrad.addColorStop(0.5, '#991B1B');
  cGrad.addColorStop(1, '#7F1D1D');
  ctx.beginPath();
  ctx.arc(0, 0, centerR, 0, 2 * Math.PI);
  ctx.fillStyle = cGrad;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, -centerR * 0.2, centerR * 0.7, 0, 2 * Math.PI);
  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  ctx.fill();

  const fontSize = Math.max(12, centerR * 0.38);
  ctx.fillStyle = '#FEF2F2';
  ctx.font = `bold ${fontSize}px Montserrat, Inter, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('SPIN', 0, -fontSize * 0.55);

  const nowSize = Math.max(15, centerR * 0.48);
  ctx.fillStyle = '#F5D76E';
  ctx.font = `900 ${nowSize}px Montserrat, Inter, sans-serif`;
  ctx.fillText('NOW', 0, nowSize * 0.45);

  ctx.restore();

  // 7. Pointer
  ctx.save();
  ctx.translate(cx, 16);
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 3;
  ctx.beginPath();
  ctx.moveTo(-14, 0);
  ctx.lineTo(14, 0);
  ctx.lineTo(0, 24);
  ctx.closePath();
  const pGrad = ctx.createLinearGradient(0, 0, 0, 24);
  pGrad.addColorStop(0, '#F5E27A');
  pGrad.addColorStop(1, '#D4AF37');
  ctx.fillStyle = pGrad;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = '#B8941E';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
}

const SpinWheelModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const ledRef = useRef(null);
  const rotationRef = useRef(0);
  const ledPhaseRef = useRef(0);
  const timerRef = useRef(null);
  const segmentsRef = useRef([]);

  const [segments, setSegments] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [redeemCode, setRedeemCode] = useState(null);

  const [canSpin, setCanSpin] = useState(false);
  const [eligible, setEligible] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');
  const [nextEligible, setNextEligible] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);

  const getSize = useCallback(() => Math.min(window.innerWidth - 48, 380), []);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const size = getSize();
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);
    return { ctx, dpr, size };
  }, [getSize]);

  // Keep segmentsRef in sync
  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape' && !spinning) onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, spinning]);

  // Fetch slices
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);

    fetch(`${API_BASE}/api/spinwin/slices`)
      .then((r) => r.json())
      .then((data) => {
        if (data.disabled) {
          setDisabled(true);
          setSegments([]);
        } else {
          const apiSegs = data.data || [];
          const colored = apiSegs.map((seg, i) => ({
            ...seg,
            color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
          }));
          setSegments(colored);
          setDisabled(false);
        }
      })
      .catch(() => setDisabled(true))
      .finally(() => setLoading(false));
  }, [isOpen]);

  // Check eligibility
  useEffect(() => {
    if (!isOpen) return;
    const userId = getUID();
    if (!userId) {
      setEligible({ eligible: false, reason: 'Not logged in' });
      setCanSpin(false);
      return;
    }

    fetch(`${API_BASE}/api/spinwin/user/eligibility?user_id=${userId}`)
      .then((r) => r.json())
      .then((data) => {
        setEligible(data);
        if (data.eligible) {
          setCanSpin(true);
          setTimeLeft('');
          setNextEligible(null);
        } else {
          setCanSpin(false);
          if (data.next_eligible) setNextEligible(new Date(data.next_eligible));
        }
      })
      .catch(() => setCanSpin(false));
  }, [isOpen]);

  // Countdown timer
  useEffect(() => {
    if (!nextEligible) return;
    const tick = () => {
      const remaining = nextEligible.getTime() - Date.now();
      if (remaining <= 0) {
        setCanSpin(true);
        setTimeLeft('');
        setNextEligible(null);
        clearInterval(timerRef.current);
      } else {
        setTimeLeft(formatTime(remaining));
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [nextEligible]);

  // Draw + LED loop
  useEffect(() => {
    if (!isOpen || segments.length === 0) return;
    const init = initCanvas();
    if (!init) return;
    const { ctx, dpr, size } = init;

    let lastLedTime = 0;
    function ledLoop(now) {
      if (now - lastLedTime > 350) {
        ledPhaseRef.current = (ledPhaseRef.current + 1) % 3;
        lastLedTime = now;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        drawWheel(ctx, size, segmentsRef.current, rotationRef.current, ledPhaseRef.current);
        ctx.restore();
      }
      ledRef.current = requestAnimationFrame(ledLoop);
    }
    ledRef.current = requestAnimationFrame(ledLoop);
    return () => { if (ledRef.current) cancelAnimationFrame(ledRef.current); };
  }, [isOpen, segments, initCanvas]);

  // Spin handler
  const handleSpin = useCallback(() => {
    if (spinning || !canSpin) return;
    const userId = getUID();
    if (!userId) {
      navigate('/login?modalType=auth-signin');
      onClose();
      return;
    }

    const segs = segmentsRef.current;
    if (!segs.length) return;

    setSpinning(true);
    setResult(null);
    setRedeemCode(null);
    if (ledRef.current) cancelAnimationFrame(ledRef.current);

    fetch(`${API_BASE}/api/spinwin/user/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data.success) {
          setSpinning(false);
          setCanSpin(false);
          return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const size = getSize();

        const winIdx = data.win_index;
        const total = segs.length;
        const arc = (2 * Math.PI) / total;
        const targetAngle = 2 * Math.PI * 7 + (total - winIdx) * arc + arc * 0.5;
        const startRotation = rotationRef.current;
        const duration = 5500;
        const startTime = performance.now();

        function animate(now) {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const ease = 1 - Math.pow(1 - progress, 4);
          const currentRotation = startRotation + targetAngle * ease;
          ledPhaseRef.current = Math.floor(now / 80) % 3;

          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.scale(dpr, dpr);
          drawWheel(ctx, size, segs, currentRotation, ledPhaseRef.current);
          ctx.restore();

          if (progress < 1) {
            animRef.current = requestAnimationFrame(animate);
          } else {
            rotationRef.current = currentRotation % (2 * Math.PI);
            setSpinning(false);
            setCanSpin(false);
            setResult(data.win_slice);
            setRedeemCode(data.redeem_code || null);

            // Resume LED loop
            let lastLedTime2 = 0;
            function resumeLed(t) {
              if (t - lastLedTime2 > 350) {
                ledPhaseRef.current = (ledPhaseRef.current + 1) % 3;
                lastLedTime2 = t;
                ctx.save();
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                ctx.scale(dpr, dpr);
                drawWheel(ctx, size, segmentsRef.current, rotationRef.current, ledPhaseRef.current);
                ctx.restore();
              }
              ledRef.current = requestAnimationFrame(resumeLed);
            }
            ledRef.current = requestAnimationFrame(resumeLed);
          }
        }
        animRef.current = requestAnimationFrame(animate);
      })
      .catch(() => setSpinning(false));
  }, [spinning, canSpin, navigate, onClose, getSize]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (ledRef.current) cancelAnimationFrame(ledRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleCollect = () => {
    if (redeemCode) navigator.clipboard?.writeText(redeemCode).catch(() => {});
    onClose();
    if (getUID()) navigate('/bonus-page');
    else navigate('/login?modalType=auth-signin');
  };

  const handleClose = () => {
    if (spinning) return;
    setResult(null);
    setRedeemCode(null);
    onClose();
  };

  const handleDeposit = () => {
    onClose();
    navigate('/wallet?mode=deposit');
  };

  if (!isOpen) return null;

  const userId = getUID();

  const getSpinBtnLabel = () => {
    if (spinning) return null; // handled separately
    if (!userId) return 'LOG IN TO SPIN';
    if (!canSpin && timeLeft) return `NEXT SPIN IN ${timeLeft}`;
    if (!canSpin) return 'NOT ELIGIBLE';
    return result ? 'SPIN AGAIN' : 'SPIN NOW';
  };

  const spinBtnDisabled = spinning || (!!userId && !canSpin);

  // Loading state
  if (loading) {
    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <div className={styles.wrapper}>
            <div className={styles.spinner} />
          </div>
        </div>
      </div>
    );
  }

  // Disabled state
  if (disabled) {
    return (
      <div className={styles.overlay} onClick={handleClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <button className={styles.closeBtn} onClick={handleClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M8 8L16 16M16 8L8 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
          <div className={styles.header}>
            <h2 className={styles.modalTitle}>WHEEL OF FORTUNE</h2>
            <p className={styles.modalSubtitle}>Spin the wheel and win amazing prizes!</p>
          </div>
          <div className={styles.wrapper}>
            <p className={styles.disabledText}>Spin wheel is currently unavailable. Check back later!</p>
            <button className={styles.primaryBtn} onClick={handleClose}>CLOSE</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Ambient glows */}
        <div className={styles.glowTop} />
        <div className={styles.glowBottom} />

        {/* Close */}
        <button className={styles.closeBtn} onClick={handleClose} disabled={spinning}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M8 8L16 16M16 8L8 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>

        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.modalTitle}>WHEEL OF FORTUNE</h2>
          <p className={styles.modalSubtitle}>Spin the wheel and win amazing prizes!</p>
        </div>

        {/* Canvas Wheel */}
        <div className={styles.canvasWrap}>
          <canvas ref={canvasRef} className={styles.canvas} />
        </div>

        {/* Result */}
        {result && (
          <div className={`${styles.resultBox} ${result.is_bad_luck ? styles.resultBad : styles.resultGood}`}>
            <span className={styles.resultText}>
              {result.is_bad_luck ? 'Better luck next time!' : `You won ${result.label}!`}
            </span>
            {redeemCode && !result.is_bad_luck && (
              <div className={styles.redeemBox}>
                <span className={styles.redeemLabel}>Your Bonus Code</span>
                <span
                  className={styles.redeemCode}
                  onClick={() => navigator.clipboard?.writeText(redeemCode).catch(() => {})}
                  title="Click to copy"
                >
                  {redeemCode}
                </span>
                <span className={styles.redeemHint}>
                  Deposit any amount & use this code to get <strong>{result.reward_pct}% extra</strong> as bonus!
                </span>
              </div>
            )}
          </div>
        )}

        {/* Eligibility / cooldown */}
        {!canSpin && !spinning && userId && eligible && !eligible.eligible && !timeLeft && (
          <p className={styles.eligibilityMsg}>
            {eligible.reason === 'No deposit found'
              ? 'Make your first deposit to unlock the spin wheel!'
              : eligible.reason === 'First deposit below minimum'
                ? "Your first deposit doesn't meet the minimum requirement"
                : eligible.reason || 'Not eligible to spin right now'}
          </p>
        )}
        {timeLeft && !spinning && (
          <p className={styles.cooldownTimer}>
            Next spin in: <strong>{timeLeft}</strong>
          </p>
        )}

        {/* Actions */}
        <div className={styles.actions}>
          <button
            className={styles.goldBtn}
            onClick={userId ? handleSpin : () => { onClose(); navigate('/login?modalType=auth-signin'); }}
            disabled={spinBtnDisabled}
          >
            {spinning ? (
              <span className={styles.spinningLabel}>
                <span className={styles.spinnerSmall} />
                SPINNING...
              </span>
            ) : getSpinBtnLabel()}
          </button>

          {result && !result.is_bad_luck && redeemCode && (
            <button className={styles.primaryBtn} onClick={handleCollect}>
              COLLECT & DEPOSIT
            </button>
          )}

          <button className={styles.depositBtn} onClick={handleDeposit}>
            Deposit to Get More Spins
          </button>

          <p className={styles.footerHint}>Deposit ₹500+ to unlock free daily spins</p>
        </div>
      </div>
    </div>
  );
};

export default SpinWheelModal;
